import { Temporal } from "temporal-polyfill-lite";
import { DateWithOptionalTime } from "../client/planner/common/DateWithOptionalTime";
/**
 * Calculate calendar-day duration between two dates.
 * Returns the number of days from start to finish (finish.date - start.date).
 */ function getCalendarDayDuration(start, finish) {
    return start.date.until(finish.date, {
        largestUnit: "days"
    }).days;
}
/**
 * Given a start/finish date change, compute the constrained result applying duration-preservation logic.
 *
 * Scenarios:
 * 1. Neither date was set → just set the new value
 * 2. Only one date was set → set the new value, clamping the other date to preserve start ≤ finish
 * 3. Both dates were set, changing start → finish = newStart + duration
 * 4. Both dates were set, changing finish:
 *    - If newFinish >= currentStart → just update finish
 *    - If newFinish < currentStart → start = newFinish - duration
 *
 * @returns The adjusted { startDateTime, finishDateTime } pair
 */ export function computeConstrainedDates(currentStart, currentFinish, changedField, newValue) {
    // Clearing a date — no constraint logic needed
    if (newValue === null) {
        if (changedField === "startDateTime") {
            return {
                startDateTime: null,
                finishDateTime: currentFinish
            };
        }
        return {
            startDateTime: currentStart,
            finishDateTime: null
        };
    }
    // If both dates were previously set, apply duration-preservation logic
    if (currentStart && currentFinish) {
        const duration = getCalendarDayDuration(currentStart, currentFinish);
        if (changedField === "startDateTime") {
            // Changing start → shift finish to preserve duration, keeping finish's time info
            const newFinishDate = newValue.date.add({
                days: duration
            });
            return {
                startDateTime: newValue,
                finishDateTime: new DateWithOptionalTime(newFinishDate, currentFinish.time, currentFinish.timezone, currentFinish.isTimeSpecified)
            };
        }
        // Changing finish
        const comparison = Temporal.PlainDate.compare(newValue.date, currentStart.date);
        if (comparison >= 0) {
            // newFinish >= currentStart → just update finish
            return {
                startDateTime: currentStart,
                finishDateTime: newValue
            };
        }
        // newFinish < currentStart → shift start back to preserve duration, keeping start's time info
        const newStartDate = newValue.date.subtract({
            days: duration
        });
        return {
            startDateTime: new DateWithOptionalTime(newStartDate, currentStart.time, currentStart.timezone, currentStart.isTimeSpecified),
            finishDateTime: newValue
        };
    }
    // Only one or neither date was set — apply the change, but enforce start <= finish invariant
    if (changedField === "startDateTime") {
        let adjustedFinish = currentFinish;
        // If finish exists and new start > finish, clamp finish to match start
        if (currentFinish && Temporal.PlainDate.compare(newValue.date, currentFinish.date) > 0) {
            adjustedFinish = new DateWithOptionalTime(newValue.date, currentFinish.time, currentFinish.timezone, currentFinish.isTimeSpecified);
        }
        return {
            startDateTime: newValue,
            finishDateTime: adjustedFinish
        };
    }
    let adjustedStart = currentStart;
    // If start exists and new finish < start, clamp start to match finish
    if (currentStart && Temporal.PlainDate.compare(newValue.date, currentStart.date) < 0) {
        adjustedStart = new DateWithOptionalTime(newValue.date, currentStart.time, currentStart.timezone, currentStart.isTimeSpecified);
    }
    return {
        startDateTime: adjustedStart,
        finishDateTime: newValue
    };
}
